import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

interface CustomGpt {
  id: string;
  name: string;
  description?: string;
  short_description?: string;
  system_prompt: string;
  model: string;
  visibility: string;
  category?: string;
  tags: string[];
  is_published: boolean;
  is_featured: boolean;
  is_system: boolean;
  avatar_emoji?: string;
  color?: string;
  usage_count: number;
  avg_rating: number;
  total_ratings: number;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export function CustomGpts() {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'mine' | 'published' | 'featured'>('all');

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [model, setModel] = useState('gpt-4.1-mini');
  const [visibility, setVisibility] = useState('private');

  const queryParams = new URLSearchParams();
  if (search) queryParams.set('search', search);
  if (filter === 'published') queryParams.set('is_published', 'true');
  if (filter === 'featured') queryParams.set('is_featured', 'true');

  const { data, isLoading } = useQuery({
    queryKey: ['custom-gpts', search, filter],
    queryFn: () => api<{ data: CustomGpt[] }>(`custom-gpts?${queryParams.toString()}`),
  });

  const createMutation = useMutation({
    mutationFn: (body: { name: string; description: string; system_prompt: string; model: string; visibility: string }) =>
      api('custom-gpts', { body }),
    onSuccess: () => {
      setShowCreate(false);
      setName('');
      setDescription('');
      setSystemPrompt('');
      setModel('gpt-4.1-mini');
      setVisibility('private');
      queryClient.invalidateQueries({ queryKey: ['custom-gpts'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (gptId: string) =>
      api(`custom-gpts/${gptId}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-gpts'] });
    },
  });

  const handleCreate = () => {
    if (!name.trim() || !systemPrompt.trim()) return;
    createMutation.mutate({
      name: name.trim(),
      description: description.trim(),
      system_prompt: systemPrompt.trim(),
      model,
      visibility,
    });
  };

  const gpts = data?.data ?? [];
  const filteredGpts = filter === 'mine'
    ? gpts.filter(g => !g.is_system)
    : gpts;

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-foreground mb-1">Custom GPTs</h1>
          <p className="text-muted-foreground">Build and manage THEO-powered assistants tailored to your workflows.</p>
        </div>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium"
        >
          Create GPT
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="mb-6 rounded-lg border bg-card p-6">
          <h3 className="text-sm font-medium mb-4">Create a new Custom GPT</h3>
          <div className="space-y-4 mb-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Ticket Helper"
                  className="w-full border rounded-md px-3 py-2 bg-background text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">Model</label>
                  <select
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    className="w-full border rounded-md px-3 py-2 bg-background text-sm"
                  >
                    <option value="gpt-4.1-mini">GPT-4.1 Mini</option>
                    <option value="gpt-4.1">GPT-4.1</option>
                    <option value="gpt-4.1-nano">GPT-4.1 Nano</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">Visibility</label>
                  <select
                    value={visibility}
                    onChange={(e) => setVisibility(e.target.value)}
                    className="w-full border rounded-md px-3 py-2 bg-background text-sm"
                  >
                    <option value="private">Private</option>
                    <option value="team">Team</option>
                    <option value="organization">Organization</option>
                  </select>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-1">Description</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What does this GPT do?"
                className="w-full border rounded-md px-3 py-2 bg-background text-sm"
              />
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-1">System Prompt</label>
              <textarea
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                placeholder="You are a helpful assistant that..."
                rows={4}
                className="w-full border rounded-md px-3 py-2 bg-background text-sm resize-none"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleCreate}
              disabled={!name.trim() || !systemPrompt.trim() || createMutation.isPending}
              className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50"
            >
              {createMutation.isPending ? 'Creating...' : 'Create GPT'}
            </button>
            <button
              onClick={() => setShowCreate(false)}
              className="px-4 py-2 rounded-md text-sm border hover:bg-muted"
            >
              Cancel
            </button>
          </div>
          {createMutation.isError && (
            <p className="mt-2 text-sm text-red-500">
              {createMutation.error instanceof Error ? createMutation.error.message : 'Failed to create GPT'}
            </p>
          )}
        </div>
      )}

      {/* Search and filters */}
      <div className="flex gap-4 mb-6">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search GPTs..."
          className="flex-1 border rounded-md px-3 py-2 bg-background text-sm"
        />
        <div className="flex rounded-md border overflow-hidden text-sm">
          {(['all', 'mine', 'published', 'featured'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-2 capitalize ${
                filter === f
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background hover:bg-muted'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* GPTs grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-48 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
      ) : filteredGpts.length === 0 ? (
        <div className="rounded-lg border bg-card p-12 text-center text-muted-foreground">
          {search ? `No GPTs found for "${search}"` : 'No Custom GPTs yet. Create one to get started.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredGpts.map((gpt) => (
            <div key={gpt.id} className="rounded-lg border bg-card p-5 hover:border-primary/50 transition-colors">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-lg shrink-0">
                  {gpt.avatar_emoji ?? '🤖'}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-foreground truncate">{gpt.name}</h3>
                    {gpt.is_system && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 shrink-0">
                        System
                      </span>
                    )}
                    {gpt.is_featured && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 shrink-0">
                        Featured
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{gpt.model}</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                {gpt.short_description ?? gpt.description ?? 'No description'}
              </p>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <div className="flex items-center gap-3">
                  <span>{gpt.usage_count} uses</span>
                  {gpt.total_ratings > 0 && (
                    <span>{gpt.avg_rating.toFixed(1)} ({gpt.total_ratings})</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                    gpt.visibility === 'organization'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : gpt.visibility === 'team'
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
                  }`}>
                    {gpt.visibility}
                  </span>
                  {!gpt.is_system && (
                    <button
                      onClick={() => {
                        if (confirm(`Delete "${gpt.name}"?`)) {
                          deleteMutation.mutate(gpt.id);
                        }
                      }}
                      className="text-red-500 hover:text-red-700"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
